import { useLingui } from '@lingui/react/macro';
import { Button } from '@repo/videa-components/src/ui/button';
import { Checkbox } from '@repo/videa-components/src/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@repo/videa-components/src/ui/dropdown-menu';

import { useGetDateFNSLocale } from '@/locales/helpers';

import type { Visit } from './visitAttachmentsTypes';
import {
  formatVisitDateCompact,
  getImageCounts,
  getImageTypeAbbreviations,
} from './visitAttachmentsUtils';

interface VisitDropdownProps {
  visits: Visit[];
  selectedVisits: number[];
  isOpen: boolean;
  onToggle: () => void;
  onToggleVisitSelection: (visit: Visit) => void;
  isLoading?: boolean;
}

export const VisitDropdown = ({
  visits,
  selectedVisits,
  onToggleVisitSelection,
  isLoading = false,
}: VisitDropdownProps) => {
  const { t } = useLingui();
  const dateLocale = useGetDateFNSLocale();

  const validVisits = visits.filter((visit) => {
    const imageTypeCounts = visit.imageTypeCounts.map((count) => ({
      type: count.type,
      count: Number(count.count),
    }));
    const imageCount = getImageCounts(imageTypeCounts);
    return imageCount > 0;
  });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          className="vai-w-full vai-justify-between vai-text-[#18181B] vai-font-normal vai-text-left"
          variant="outline"
          disabled={isLoading}
        >
          <p className="vai-text-ellipsis vai-overflow-hidden vai-whitespace-nowrap vai-max-w-[90%]">
            {isLoading
              ? t`Loading visits...`
              : selectedVisits.length === 0
                ? t`Select visits`
                : `${selectedVisits.length} ${
                    selectedVisits.length === 1 ? t`visit` : t`visits`
                  } ${t`selected`}`}
          </p>
          {isLoading && (
            <div className="vai-animate-spin vai-rounded-full vai-h-4 vai-w-4 vai-border-b-2 vai-border-sapphire-600 vai-ml-2"></div>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="vai-w-[--radix-popper-anchor-width] vai-overflow-y-auto vai-max-h-[65vh] vai-flex vai-flex-col vai-gap-2 vai-p-2"
      >
        {isLoading && (
          <DropdownMenuLabel className="vai-flex vai-items-center vai-justify-center vai-gap-2 vai-py-4">
            <div className="vai-animate-spin vai-rounded-full vai-h-4 vai-w-4 vai-border-b-2 vai-border-sapphire-600"></div>
            <span>{t`Loading visits...`}</span>
          </DropdownMenuLabel>
        )}
        {!isLoading && !validVisits.length && (
          <DropdownMenuLabel>{t`No visits with images found`}</DropdownMenuLabel>
        )}
        {!isLoading &&
          validVisits.map((visit) => {
            const isSelected = selectedVisits.includes(visit.id ?? 0);
            const imageTypeCounts = visit.imageTypeCounts.map((count) => ({
              type: count.type,
              count: Number(count.count),
            }));
            const imageCount = getImageCounts(imageTypeCounts);
            const imageTypes = getImageTypeAbbreviations(imageTypeCounts);
            const formattedDate = visit.visitDate
              ? formatVisitDateCompact(
                  typeof visit.visitDate === 'string'
                    ? visit.visitDate
                    : visit.visitDate.toISOString(),
                  dateLocale,
                )
              : '';

            return (
              <Button
                key={visit.id}
                className="vai-w-full !vai-justify-start !vai-bg-inherit hover:!vai-bg-slate-100 vai-py-2"
                onClick={() => onToggleVisitSelection(visit)}
              >
                <Checkbox
                  className="vai-border-sapphire-600 data-[state=checked]:vai-bg-sapphire-600"
                  checked={isSelected}
                />
                <div className="vai-flex vai-flex-col vai-ml-2 vai-w-full">
                  <span className="vai-font-medium vai-text-black vai-text-left vai-w-full">
                    {formattedDate}
                  </span>
                  <span className="vai-text-xs vai-text-gray-500 vai-text-left vai-w-full">
                    {imageCount} {t`images`} ({imageTypes})
                  </span>
                </div>
              </Button>
            );
          })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
